import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../config/ConfigContext';
import { extractVideoId } from '../services/youtubeService';
import { buildDriveStreamUrl } from '../utils/driveStreamUrl';

/**
 * ShortCard component to display short video information in a vertical card format
 * @param {Object} props - Component props
 * @param {Object} props.short - Short data object
 * @returns {JSX.Element} ShortCard component
 */
const ShortCard = ({ short, isMobileGrid = false }) => {
  const navigate = useNavigate();
  const { theme } = useConfig();
  const [isHovering, setIsHovering] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const hoverTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  // Detect if this is a Drive-based lecture clip vs YouTube short
  const isClip = !!short?.isClip || !!short?.driveFileId;

  // Extract video ID for thumbnail (null for Drive clips)
  const videoId = isClip ? null : extractVideoId(short?.shortUrl);

  // Detect mobile on mount
  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      // Clear timeouts on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Define color schemes for different series/events
  const colors = {
    'Ramadan': '#4CAF50,#2E7D32', // Green gradient for Ramadan
    'Muharram': '#9C27B0,#7B1FA2', // Purple gradient for Muharram
    'default': '#FF5722,#D84315' // Orange gradient for shorts by default
  };

  // Get color scheme based on series/event
  const getColorScheme = () => {
    const eventKey = short?.series || short?.eventName || 'default';
    let colorPair = colors.default;
    
    Object.keys(colors).forEach(key => {
      if (eventKey.toLowerCase().includes(key.toLowerCase())) {
        colorPair = colors[key];
      }
    });
    
    return colorPair;
  };

  // Handle click - different behavior for clips vs shorts, mobile vs desktop
  const handleClick = (e) => {
    // Drive clips: always navigate directly to the lecture-clips player
    if (isClip) {
      navigate(`/lecture-clips?v=${encodeURIComponent(short.driveFileId)}`);
      return;
    }

    // Check if clicking on play button or card on mobile
    const isPlayButton = e.target.closest('.play-button-overlay');

    if (isMobile) {
      if (!isPlaying && videoId) {
        // Start preview on mobile tap
        e.stopPropagation();
        e.preventDefault();
        setIsLoading(true);
        setShowVideo(true);

        // Set loading timeout to mark as playing after a brief delay
        loadingTimeoutRef.current = setTimeout(() => {
          setIsLoading(false);
          setIsPlaying(true);
        }, 800);
      } else if (isPlaying) {
        // Toggle pause/play on tap when already playing
        e.stopPropagation();
        e.preventDefault();
        setShowVideo(false);
        setIsPlaying(false);
        setIsLoading(false);

        // Clear loading timeout
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      } else if (!videoId) {
        // No video ID, navigate to full view
        navigate(`/shorts?v=${encodeURIComponent(short.shortUrl)}`);
      }
    } else {
      // Desktop: navigate on click (hover handles preview)
      if (!isHovering) {
        navigate(`/shorts?v=${encodeURIComponent(short.shortUrl)}`);
      }
    }
  };

  // Handle navigation to full view (for mobile "Watch Full" button)
  const handleNavigate = () => {
    if (isClip) {
      navigate(`/lecture-clips?v=${encodeURIComponent(short.driveFileId)}`);
    } else {
      navigate(`/shorts?v=${encodeURIComponent(short.shortUrl)}`);
    }
  };

  // Handle mouse enter - start timer to show video
  const handleMouseEnter = (e) => {
    // Desktop only hover-to-play
    if (!isMobile) {
      setIsHovering(true);
      
      // Start timer to show video after 500ms hover
      hoverTimeoutRef.current = setTimeout(() => {
        if (videoId) {
          setIsLoading(true);
          setShowVideo(true);
          
          // Simulate loading time
          setTimeout(() => {
            setIsLoading(false);
          }, 600);
        }
      }, 500);
    }
  };

  // Handle mouse leave - cancel timer and hide video
  const handleMouseLeave = (e) => {
    setIsHovering(false);
    setShowVideo(false);
    setIsLoading(false);
    
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Generate thumbnail URL — YouTube for shorts, Drive proxy for clips
  const colorPair = getColorScheme();
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null; // Clips use <video> element with Drive proxy (set below)

  // Ref for Drive clip video thumbnail (seeks to 1s for a frame grab, like LectureHighlights ClipCard)
  const clipVideoRef = useRef(null);
  useEffect(() => {
    if (!isClip || !short?.driveFileId || !clipVideoRef.current) return;
    const video = clipVideoRef.current;
    if (video.src) return; // Already loaded
    video.src = buildDriveStreamUrl(short.driveFileId);
    video.preload = 'metadata';
    video.play().catch(() => {});
  }, [isClip, short?.driveFileId]);

  return (
    <div 
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        boxShadow: isHovering 
          ? '0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.08)'
          : '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        position: 'relative',
        transform: isHovering ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Vertical Thumbnail Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        paddingBottom: isMobileGrid ? '0' : '177.78%', // Full height for grid, 9:16 for regular
        height: isMobileGrid ? '100%' : 'auto',
        backgroundColor: '#f0f0f0',
        overflow: 'hidden'
      }}>
        {/* Show video preview on hover or thumbnail */}
        {showVideo && videoId ? (
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=0&showinfo=0&modestbranding=1&loop=1&playlist=${videoId}`}
            title={short?.title || 'Short video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          /* Thumbnail Image or Drive video frame */
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: thumbnailUrl
              ? `url(${thumbnailUrl})`
              : (!isClip ? `linear-gradient(145deg, ${theme.colors.primary}dd 0%, ${theme.colors.primary}88 50%, ${theme.colors.primary}bb 100%)` : 'none'),
            backgroundColor: isClip ? '#111' : '#f0f0f0',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}>
            {/* Drive clip video thumbnail — loads via proxy, seeks to 1s for frame grab */}
            {isClip && short?.driveFileId && (
              <video
                ref={clipVideoRef}
                muted
                playsInline
                onLoadedMetadata={(e) => { e.target.currentTime = 1; }}
                onSeeked={(e) => { e.target.pause(); e.target.style.opacity = '1'; }}
                style={{
                  position: 'absolute', top: 0, left: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  opacity: 0,
                  transition: 'opacity 0.4s ease',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
            )}
            {/* Play/Pause button overlay */}
            {(!isHovering || isMobile) && !showVideo && (
              <div 
                className="play-button-overlay"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: isMobile ? '56px' : '48px',
                  height: isMobile ? '56px' : '48px',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.9,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  backdropFilter: 'blur(8px)',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  animation: isMobile && !isPlaying ? 'pulse 2s infinite' : 'none',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                }}>
                {isPlaying ? (
                  // Pause icon for mobile when playing
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  // Play icon
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </div>
            )}
          </div>
        )}

        
        {/* Short badge / Playing indicator */}
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: isPlaying 
            ? 'linear-gradient(135deg, rgba(255, 59, 48, 0.95), rgba(255, 45, 85, 0.95))'
            : 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(30, 30, 30, 0.8))',
          color: 'white',
          padding: '6px 10px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 1,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
          animation: isPlaying ? 'glow 1.5s ease-in-out infinite' : 'none',
          transition: 'all 0.3s ease'
        }}>
          {isPlaying ? (
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#fff',
              borderRadius: '50%',
              animation: 'pulse 1s infinite'
            }} />
          ) : (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            </svg>
          )}
          {isPlaying ? 'PLAYING' : isClip ? 'CLIP' : 'SHORT'}
        </div>
        
        {/* Action buttons for mobile when playing */}
        {isMobile && isPlaying && (
          <div style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '8px',
            zIndex: 4, // Increased to be above title
          }}>
            {/* Unmute/Mute Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted(!isMuted);
              }}
              style={{
                background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primary}dd)`,
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: '25px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
            >
              {isMuted ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                  </svg>
                  Unmute
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                  </svg>
                  Mute
                </>
              )}
            </button>
            
            {/* Full Player Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNavigate();
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                color: theme.colors.primary,
                border: `2px solid ${theme.colors.primary}`,
                padding: '10px 16px',
                borderRadius: '25px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s ease'
              }}
            >
              Full Player
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
              </svg>
            </button>
          </div>
        )}
        
        {/* Hover indicator for desktop */}
        {isHovering && !showVideo && !isMobile && (
          <div style={{
            position: 'absolute',
            bottom: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(30, 30, 30, 0.9))',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '15px',
            fontSize: '11px',
            fontWeight: '500',
            zIndex: 1,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            animation: 'fadeIn 0.3s ease'
          }}>
            Keep hovering to preview
          </div>
        )}
        
        {/* Loading indicator */}
        {isLoading && videoId && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              borderTop: '3px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}
        
        {/* Title Overlay for Mobile (both grid and regular) - Inside thumbnail container */}
        {(isMobileGrid || isMobile) && (
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 50%, transparent 100%)',
            padding: isMobileGrid ? '16px 10px 10px' : '20px 12px 12px',
            zIndex: isPlaying ? 1 : 2 // Lower z-index when playing so buttons are on top
          }}>
            <h4 style={{
              margin: 0,
              fontSize: isMobileGrid ? '13px' : '14px',
              fontWeight: '700',
              color: 'white',
              lineHeight: '1.35',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textShadow: '0 2px 4px rgba(0,0,0,0.9)'
            }}>
              {short?.title || 'Untitled Short'}
            </h4>
          </div>
        )}
      </div>

      {/* Content Section - Only show on desktop */}
      {!isMobile && (
        <div style={{
          padding: '14px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(250,250,250,0.5) 100%)',
          position: 'relative'
        }}>
          {/* Title — card bg is always white so text must be dark regardless of page theme */}
          <h4 style={{
            margin: '0 0 8px 0',
            fontSize: '14px',
            fontWeight: '700',
            color: theme.isDarkBackground ? '#333333' : theme.colors.text,
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minHeight: '40px',
            transition: 'color 0.2s ease'
          }}>
            {short?.title || 'Untitled Short'}
          </h4>

          {/* Speaker — use safe dark color since darkGray gets lightified on dark-themed pages */}
          {short?.speakerName && (
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: theme.isDarkBackground ? '#666666' : (theme.colors.darkGray || '#666'),
              fontWeight: '500'
            }}>
              {short.speakerName}
            </p>
          )}

          {/* Series/Event label */}
          {short?.eventName && short.eventName.toLowerCase() !== 'all' && (
            <div style={{
              marginTop: '8px',
              fontSize: '11px',
              color: theme.colors.linkColor || theme.colors.primary,
              background: `linear-gradient(135deg, ${theme.colors.linkColor || theme.colors.primary}10 0%, ${theme.colors.linkColor || theme.colors.primary}15 100%)`,
              padding: '4px 8px',
              borderRadius: '6px',
              display: 'inline-block',
              alignSelf: 'flex-start',
              fontWeight: '600',
              border: `1px solid ${theme.colors.linkColor || theme.colors.primary}20`,
              transition: 'all 0.2s ease'
            }}>
              {short.eventName}
            </div>
          )}
        </div>
      )}
      
      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          }
        }
        
        @keyframes glow {
          0% {
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          }
          50% {
            box-shadow: 0 2px 20px rgba(255, 59, 48, 0.4);
          }
          100% {
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ShortCard;